import { useState } from 'react'
import { useAuthStore } from '../../stores/auth'
import { authApi } from '../../api/endpoints'

export default function LoginPage() {
  const { login, checkAuth, setupComplete } = useAuthStore()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // OTP state
  const [otpSessionId, setOtpSessionId] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [otpStep, setOtpStep] = useState(false)
  const [resending, setResending] = useState(false)

  const isSetup = !setupComplete

  // After successful auth, return to a same-origin `next` path if present
  // (used by the MCP OAuth consent flow). Only relative paths are honored.
  const redirectAfterAuth = () => {
    const next = new URLSearchParams(window.location.search).get('next')
    // Same-origin relative paths only. Reject protocol-relative ("//") and
    // backslash forms ("/\") that browsers normalize into external redirects.
    if (next && next.startsWith('/') && !next.startsWith('//') && !next.includes('\\')) {
      const u = new URL(next, window.location.origin)
      if (u.origin === window.location.origin) {
        window.location.href = u.pathname + u.search
      }
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (isSetup) {
        const { setup } = useAuthStore.getState()
        await setup(username, password)
      } else {
        await login(username, password)
      }
      redirectAfterAuth()
    } catch (err: any) {
      if (err.otpRequired) {
        setOtpSessionId(err.otpSessionId)
        setOtpStep(true)
        setOtpCode('')
      } else {
        setError(err.message || 'Authentication failed')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await authApi.verifyOtp(otpSessionId, otpCode)
      await checkAuth()
      redirectAfterAuth()
    } catch (err: any) {
      setError(err.message || 'OTP verification failed')
      // If session expired or max attempts, go back to login
      if (err.message?.includes('login again') || err.message?.includes('expired')) {
        setOtpStep(false)
        setOtpSessionId('')
        setOtpCode('')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleResendOtp = async () => {
    setResending(true)
    setError('')
    try {
      const resp = await authApi.resendOtp(otpSessionId)
      setOtpSessionId(resp.otp_session_id)
      setOtpCode('')
      setError('') // clear any previous error
    } catch (err: any) {
      setError(err.message || 'Failed to resend OTP')
      if (err.message?.includes('expired') || err.message?.includes('login again')) {
        setOtpStep(false)
        setOtpSessionId('')
      }
    } finally {
      setResending(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-indigo-500 rounded-xl flex items-center justify-center text-white font-bold text-lg mx-auto mb-3">
              ON
            </div>
            <h1 className="text-xl font-semibold text-gray-900">
              {isSetup ? 'Create Admin Account' : otpStep ? 'Enter OTP' : 'Sign In'}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {isSetup
                ? 'Set up your OpsNest admin account'
                : otpStep
                  ? 'A verification code was sent to your phone'
                  : 'Welcome back to OpsNest'}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
              {error}
            </div>
          )}

          {!otpStep ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  required
                  minLength={6}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Please wait...' : isSetup ? 'Create Account' : 'Sign In'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Verification Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-center text-2xl tracking-[0.5em] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  placeholder="------"
                  required
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={loading || otpCode.length !== 6}
                className="w-full py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Verifying...' : 'Verify OTP'}
              </button>
              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resending}
                  className="text-indigo-600 hover:text-indigo-700 font-medium disabled:opacity-50"
                >
                  {resending ? 'Sending...' : 'Resend OTP'}
                </button>
                <button
                  type="button"
                  onClick={() => { setOtpStep(false); setOtpSessionId(''); setOtpCode(''); setError('') }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Back to login
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
